import React, { useEffect, useMemo, useState } from "react";
import { getExpertHomeParams, getMediaParam, MediaKeys, setMediaParam } from "../../../../helpers/storage";
import { loadSettings } from "../../../../helpers/settings";
import { appMessage } from "../../../../helpers/message";

export interface MediaContextState {
  audioStreamEnabled: boolean;
  setAudioStreamEnabled: (enabled: boolean, save?: boolean) => void;
  videoStreamEnabled: boolean;
  setVideoStreamEnabled: (enabled: boolean, save?: boolean) => void;
  screensVideoStreamEnabled: boolean;
  setScreensVideoStreamEnabled: (enabled: boolean, save?: boolean) => void;
  boardsVideoStreamEnabled: boolean;
  setBoardsVideoStreamEnabled: (enabled: boolean, save?: boolean) => void;
  syncMicrophone: boolean;
  setSyncMicrophone: (value: boolean) => void;
}

const MediaContext = React.createContext<MediaContextState | undefined>(undefined);

export const useMediaContext = (): MediaContextState => {
  const context = React.useContext(MediaContext);
  if (!context) {
    throw new Error("useMediaContext must be used within a MediaContextProvider");
  }
  return context;
};

export const MediaContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [initialSettings] = useState({ current: loadSettings() });
  const [audioStreamEnabled, setAudioStreamEnabled] = useState(initialSettings.current.defaultValuesAtStart?.audio ?? getMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED) ?? false);
  const [videoStreamEnabled, setVideoStreamEnabled] = useState(initialSettings.current.defaultValuesAtStart?.cameras ?? getMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED) ?? false);
  const [screensVideoStreamEnabled, setScreensVideoStreamEnabled] = useState(initialSettings.current.defaultValuesAtStart?.screens ?? getMediaParam(MediaKeys.INTPUT_SCREENS_VIDEO_ENABLED) ?? false);
  const [boardsVideoStreamEnabled, setBoardsVideoStreamEnabled] = useState(initialSettings.current.defaultValuesAtStart?.boards ?? getMediaParam(MediaKeys.INTPUT_BOARDS_VIDEO_ENABLED) ?? false);
  const [syncMicrophone, setSyncMicrophone] = useState(false);

  useEffect(() => {
    if (syncMicrophone) appMessage.setMicrophoneData(audioStreamEnabled ? false : true);
    else {
      const expertHomeParams = getExpertHomeParams();
      const micOffOutside = expertHomeParams?.micOffOutsideOfAnEvent ?? false;
      appMessage.setMicrophoneData(micOffOutside);
    }
  }, [syncMicrophone, audioStreamEnabled]);


  const state = useMemo<MediaContextState>(() => ({
    audioStreamEnabled,
    setAudioStreamEnabled: (enabled, save=true) => {
      if (save) setMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED, enabled);
      setAudioStreamEnabled(enabled);
    },
    videoStreamEnabled,
    setVideoStreamEnabled: (enabled, save=true) => {
      if (save) setMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED, enabled);
      setVideoStreamEnabled(enabled);
    },
    screensVideoStreamEnabled,
    setScreensVideoStreamEnabled: (enabled, save=true) => {
      if (save) setMediaParam(MediaKeys.INTPUT_SCREENS_VIDEO_ENABLED, enabled);
      setScreensVideoStreamEnabled(enabled);
    },
    boardsVideoStreamEnabled,
    setBoardsVideoStreamEnabled: (enabled, save=true) => {
      if (save) setMediaParam(MediaKeys.INTPUT_BOARDS_VIDEO_ENABLED, enabled);
      setBoardsVideoStreamEnabled(enabled);
    },

    syncMicrophone,
    setSyncMicrophone,
  }), [syncMicrophone, audioStreamEnabled, videoStreamEnabled, screensVideoStreamEnabled, boardsVideoStreamEnabled]);

  return (
    <MediaContext.Provider
      value={state}
    >
      {children}
    </MediaContext.Provider>
  );
};